import React, { useLayoutEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { Download, Lock, RefreshCw } from "lucide-react";

import LayerdIcon from "../../../common/LayerdIcon";
import V2PageHeader from "../../../common/V2PageHeader";
import { GhostButton } from "../../../common/Button";
import { ReactComponent as LoadingIcon } from "../../../../vendor/_new/icon/loading.svg";
import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import useAuth from "../../../../hooks/useAuth";
import useHome from "../../../../hooks/user/useHome";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import { useNotification } from "../../../../contexts/NotificationContext";
import { ALL_SITES_ID, LAST_SINGLE_SITE_STORAGE_KEY } from "../../../../utils/globalSite";
import SSLOverviewPage from "./SSLOverviewPage";
import { pageDescriptionSx } from "../../../common/V2PageHeader";
import { CERTS_PAGE_SHELL_SPACING } from "./certsLayout";
import { SSL_TLS_CONFIGURATION } from "./sslTlsPaths";
import { downloadSslOverviewCsv } from "./sslOverviewExport";

export default function SSLTlsOverviewPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems } = useSite();
  const { applySiteSelectorUi, curProjectId } = useHome();
  const { getSSLOverview } = useSSLConfig();
  const { showError, showSuccess } = useNotification();
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useLayoutEffect(() => {
    applySiteSelectorUi({ enabled: false, showAllOption: true, curSiteId: ALL_SITES_ID });
  }, [applySiteSelectorUi]);

  React.useEffect(() => {
    if (isAuthenticated && !siteList) getSitesForItems();
  }, [isAuthenticated, siteList, getSitesForItems]);

  const onSelectSite = (site) => {
    if (!site?.site_uid) return;
    const match = siteList?.find((s) => s.id === site.site_uid);
    if (match?.site_id) {
      try {
        sessionStorage.setItem(LAST_SINGLE_SITE_STORAGE_KEY, match.site_id);
      } catch {
        // ignore
      }
    }
    navigate(`${SSL_TLS_CONFIGURATION}?site_uid=${encodeURIComponent(site.site_uid)}`);
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await getSSLOverview(curProjectId);
      if (res?.status !== "success") {
        showError(typeof res?.data === "string" ? res.data : "Failed to load certificate data");
        return;
      }
      const sites = res.data?.sites || [];
      if (!sites.length) {
        showError("No certificate data to export");
        return;
      }
      downloadSslOverviewCsv(sites);
      showSuccess(`Exported ${sites.length} site${sites.length === 1 ? "" : "s"}`);
    } catch (e) {
      showError(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }, [curProjectId, getSSLOverview, showError, showSuccess]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    window.setTimeout(() => setRefreshing(false), 1000);
  }, []);

  return (
    <>
      <Helmet title="SSL / TLS · Overview" />
      <Stack
        spacing={CERTS_PAGE_SHELL_SPACING}
        sx={{ p: 4, fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
      >
        <V2PageHeader
          icon={Lock}
          title="SSL / TLS Overview"
          description="Certificate health, encryption posture and TLS configuration across every site on your account."
          sx={{ pb: 0 }}
          actions={(
            <Stack direction="row" spacing={1} alignItems="center">
              <GhostButton
                disabled={refreshing}
                startIcon={
                  refreshing ? (
                    <LoadingIcon style={{ color: colors.neutral[600], width: 16, height: 16 }} />
                  ) : (
                    <LayerdIcon icon={RefreshCw} size="sm" color="neutral" />
                  )
                }
                onClick={handleRefresh}
              >
                Refresh
              </GhostButton>
              <GhostButton
                startIcon={<LayerdIcon icon={Download} size="sm" color="neutral" />}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Exporting…" : "Export"}
              </GhostButton>
            </Stack>
          )}
        />

        {siteList && (
          <Box>
            <Typography sx={pageDescriptionSx}>
              Account-wide view across <b>{siteList.length} site{siteList.length === 1 ? "" : "s"}</b>
            </Typography>
          </Box>
        )}

        <SSLOverviewPage
          key={`ov-${refreshKey}`}
          onSelectSite={onSelectSite}
          refreshTrigger={refreshKey}
        />
      </Stack>
    </>
  );
}
